import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, Badge, Avatar } from '@/backoffice/components/ui';
import { formatCurrency, formatDate, formatTime } from '@/backoffice/utils';
import { fetchCommunityTrips, type CommunityTrip } from '@/domains/client/services/client.service';
import {
  Users,
  Clock,
  Package,
  ArrowRight,
  Calendar,
  Star,
  MessageSquare,
  Loader2,
} from 'lucide-react';

export function NeighborsPage() {
  const [trips, setTrips] = useState<CommunityTrip[]>([]);
  const [loading, setLoading] = useState(true);
  const [cityFilter, setCityFilter] = useState('');

  useEffect(() => {
    fetchCommunityTrips()
      .then(setTrips)
      .catch(() => setTrips([]))
      .finally(() => setLoading(false));
  }, []);

  const cities = [...new Set(trips.flatMap((t) => [t.from.city, t.to.city]))].sort();
  const availableTrips = trips.filter((t) => {
    if (!cityFilter) return t.status === 'planned';
    return t.status === 'planned' && (t.from.city === cityFilter || t.to.city === cityFilter);
  });

  return (
    <DashboardLayout userType="client" title="Voisins disponibles">
      <Card className="mb-6 bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-lg bg-emerald-200 flex items-center justify-center">
            <Users className="w-6 h-6 text-emerald-700" />
          </div>
          <div>
            <h3 className="font-semibold text-emerald-900">Livraison communautaire</h3>
            <p className="text-sm text-emerald-800 mt-1">
              Profitez de trajets partages par vos voisins. Economic, ecologique et convivial.
              Des membres de votre communaute se rendent dans differentes zones et peuvent transporter vos colis.
            </p>
            <div className="flex items-center gap-4 mt-3">
              <div className="flex items-center gap-1 text-sm text-emerald-700">
                <Badge variant="success" size="sm">-40%</Badge>
                <span>moins cher</span>
              </div>
              <div className="flex items-center gap-1 text-sm text-emerald-700">
                <Star className="w-4 h-4 fill-emerald-500" />
                <span>Contributeurs verifies</span>
              </div>
            </div>
          </div>
        </div>
      </Card>

      <div className="flex items-center gap-4 mb-6">
        <select
          value={cityFilter}
          onChange={(e) => setCityFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Toutes les villes</option>
          {cities.map((city) => (
            <option key={city} value={city}>{city}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Chargement...
        </div>
      ) : availableTrips.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun trajet disponible</h3>
            <p className="text-gray-500">
              Il n'y a pas de livreurs communautaires actifs dans votre zone pour le moment.
            </p>
          </div>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {availableTrips.map((trip) => (
            <TripCard key={trip.id} trip={trip} />
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}

function TripCard({ trip }: { trip: CommunityTrip }) {
  const departureDate = new Date(trip.departure_date);

  return (
    <Card className="flex flex-col">
      <div className="flex items-center gap-3 mb-4">
        <Avatar name={trip.driver_name} size="lg" />
        <div>
          <p className="font-medium text-gray-900">{trip.driver_name}</p>
          <div className="flex items-center gap-1">
            <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
            <span className="text-sm font-medium text-gray-700">{trip.rating}</span>
            <span className="text-sm text-gray-500">({trip.deliveries_count} livraisons)</span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-500">Depart</p>
          <p className="font-medium text-gray-900 truncate">{trip.from.city}</p>
        </div>
        <ArrowRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
        <div className="flex-1 min-w-0 text-right">
          <p className="text-sm text-gray-500">Destination</p>
          <p className="font-medium text-gray-900 truncate">{trip.to.city}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Calendar className="w-4 h-4" />
          <span>{formatDate(departureDate, 'short')}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Clock className="w-4 h-4" />
          <span>{formatTime(departureDate)}</span>
        </div>
      </div>

      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg mb-4">
        <div className="flex items-center gap-2">
          <Package className="w-4 h-4 text-gray-500" />
          <span className="text-sm text-gray-600">Capacite disponible</span>
        </div>
        <Badge variant="success" size="lg">{trip.available_space} kg</Badge>
      </div>

      <div className="flex items-center justify-between mb-4">
        <span className="text-gray-600">Prix par kg</span>
        <p className="text-xl font-bold text-emerald-600">{formatCurrency(trip.price_per_kg)}</p>
      </div>

      <div className="mt-auto flex gap-2">
        <Button variant="outline" className="flex-1" leftIcon={<MessageSquare className="w-4 h-4" />} disabled>
          Contacter
        </Button>
        <Button className="flex-1" leftIcon={<Package className="w-4 h-4" />} disabled>
          Reserver
        </Button>
      </div>
    </Card>
  );
}
